/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import React, { useEffect, useRef, useState } from 'react'

import { FlowConnectorLine, FlowConnectorLineProps } from '@/components/ui/flow/FlowConnectorLine'
import { cn } from '@/utils/css'

/** A description of the available properties for this component. */
type FlowContentWrapperProps = {
  /** The children to render within the flow content wrapper. */
  children: Array<React.ReactNode>

  /** The direction to start the flow in. */
  startingDirection: 'left' | 'right'

  /** The properties to pass to the flow connector line. */
  flowLineProps: Omit<FlowConnectorLineProps, 'direction' | 'horizontalWidth'>

  /** Whether the flow line(s) should be hidden on mobile. */
  flowLineHiddenOnMobile: boolean
}

/**
 * A component that wraps flow content items and connects them with a line. The direction of the line alternates between
 * left-to-right and right-to-left.
 */
const FlowContentWrapper: React.FC<FlowContentWrapperProps> = ({
  children,
  startingDirection,
  flowLineProps,
  flowLineHiddenOnMobile,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [horizontalWidth, setHorizontalWidth] = useState(0)

  useEffect(() => {
    const currentContainer = containerRef.current
    const observer = new ResizeObserver((entries) => {
      // Only track the first entry (our container element)
      const entry = entries[0]
      if (entry) {
        setHorizontalWidth(entry.contentRect.width)
      }
    })

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    // Clean up observer on unmount
    return () => {
      if (currentContainer) {
        observer.unobserve(currentContainer)
      }
    }
  }, [])

  return (
    <div className={'flex flex-col items-stretch max-w-full px-4 gap-8 mx-auto'} ref={containerRef}>
      {children.map((child, index) => {
        const isLast = index === children.length - 1
        const isEven = index % 2 === 0
        const direction = isEven ? startingDirection : startingDirection === 'left' ? 'right' : 'left'

        return (
          <React.Fragment key={index}>
            <div className={`w-full flex ${direction === 'left' ? 'justify-start' : 'justify-end'}`}>{child}</div>
            {!isLast && (
              <div className={cn(flowLineHiddenOnMobile && 'hidden md:block', 'my-4 mx-auto')}>
                <FlowConnectorLine
                  {...flowLineProps}
                  horizontalWidth={horizontalWidth}
                  direction={direction === 'left' ? 'leftToRight' : 'rightToLeft'}
                />
              </div>
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}

export { FlowContentWrapper }
